import type { ReferencePaper } from "./types";

export interface ExtractedPaper extends ReferencePaper {
  text: string;
}

const PER_PAPER_LIMIT = 36_000;
const TOTAL_LIMIT = 120_000;

function normalizeText(text: string) {
  return text
    .replace(/\u0000/g, "")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export async function extractReferencePapers(
  files: File[],
): Promise<ExtractedPaper[]> {
  if (files.length < 1 || files.length > 5) {
    throw new Error("请选择 1–5 篇论文。");
  }
  let remaining = TOTAL_LIMIT;
  const results: ExtractedPaper[] = [];

  for (const file of files) {
    if (file.size > 30 * 1024 * 1024) {
      throw new Error(`${file.name} 超过 30MB，请换用压缩后的 PDF。`);
    }
    const extension = file.name.split(".").pop()?.toLowerCase();
    let text = "";
    let pageCount = 1;

    if (extension === "pdf" || file.type === "application/pdf") {
      const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
      const worker = await import(
        "pdfjs-dist/legacy/build/pdf.worker.min.mjs?url"
      );
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      const document = await pdfjs.getDocument({
        data: new Uint8Array(await file.arrayBuffer()),
      }).promise;
      pageCount = document.numPages;
      const pages: string[] = [];
      for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
        const page = await document.getPage(pageNumber);
        const content = await page.getTextContent();
        pages.push(
          content.items
            .map((item) => ("str" in item ? item.str : ""))
            .join(" "),
        );
        if (pages.join("\n").length >= PER_PAPER_LIMIT) break;
      }
      text = pages.join("\n\n");
      await document.destroy();
    } else if (["txt", "md"].includes(extension || "")) {
      text = await file.text();
    } else {
      throw new Error(`${file.name} 不是支持的 PDF、TXT 或 Markdown 文件。`);
    }

    const normalized = normalizeText(text);
    if (normalized.length < 500) {
      throw new Error(
        `${file.name} 可提取文字过少；扫描版 PDF 请先进行 OCR。`,
      );
    }
    const bounded = normalized.slice(0, Math.min(PER_PAPER_LIMIT, remaining));
    remaining -= bounded.length;
    results.push({
      id: crypto.randomUUID(),
      name: file.name,
      size: file.size,
      pageCount,
      charCount: bounded.length,
      extractedAt: new Date().toISOString(),
      text: bounded,
    });
    if (remaining <= 0) break;
  }
  return results;
}
